import { useCallback, useEffect } from 'react';

import { useShallow } from 'zustand/react/shallow';

import { useMicStore } from '@/entities/audio-recording';
import {
    type MediaDeviceData,
    type MicStreamData,
    type MicsData,
    watchMicStream,
    watchMics,
} from '@/shared/lib/media-devices';
import { watchPermission } from '@/shared/lib/permissions';

export function useMic() {
    const {
        accessDenied,
        currentMicId,
        selectMic,
        _denyAccess,
        _grantAccess,
        _setMics,
        _disableMic,
        _stopStream,
    } = useMicStore(
        useShallow((x) => ({
            accessDenied: x.accessDenied,
            currentMicId: x.currentMicId,
            selectMic: x.selectMic,
            _denyAccess: x._denyAccess,
            _grantAccess: x._grantAccess,
            _setMics: x._setMics,
            _disableMic: x._disableMic,
            _stopStream: x._stopStream,
        })),
    );

    const handlePermissionChange = useCallback(
        (permissionGranted: boolean) => {
            if (permissionGranted) {
                _grantAccess();
            } else {
                _denyAccess();
            }
        },
        [_grantAccess, _denyAccess],
    );

    const handleMicsChange = useCallback(
        (data: MediaDeviceData<MicsData>) => {
            if (data.permissionGranted) {
                _setMics(data.devices.map((x) => ({ deviceId: x.deviceId, name: x.label })));
            } else {
                _setMics([]);
            }
        },
        [_setMics],
    );

    const handleMicStreamChange = useCallback(
        (data: MediaDeviceData<MicStreamData>) => {
            if (data.permissionGranted) {
                if (!data.stream) {
                    _disableMic();
                } else {
                    selectMic(data.trackDeviceId || '', data.stream);
                }
            } else {
                _denyAccess();
            }
        },
        [_disableMic, selectMic, _denyAccess],
    );

    useEffect(() => {
        if (accessDenied) return;

        const controller = new AbortController();
        watchMicStream(currentMicId, handleMicStreamChange, controller.signal);
        return () => {
            controller.abort();
            _stopStream();
        };
    }, [accessDenied, currentMicId, _stopStream, handleMicStreamChange]);

    useEffect(() => {
        if (accessDenied) return;

        const controller = new AbortController();
        watchMics(handleMicsChange, controller.signal);
        return () => controller.abort();
    }, [accessDenied, handleMicsChange]);

    useEffect(() => {
        const controller = new AbortController();
        watchPermission('microphone', handlePermissionChange, controller.signal);
        return () => controller.abort();
    }, [handlePermissionChange]);
}
